// AGC: LoteCardPremium - Premium LOD card with DJI Action 5 Pro inspired design
// AGC: Full-bleed crop background image with gradient overlay and circular progress
// AGC: Offline-first with graceful fallback for missing images
// AGC: Shows weather info (temp + rain 24h) and phenological stage

import React, { useState, useMemo } from 'react'
import { Thermometer, CloudRain, Sprout } from 'lucide-react'
import { CircularProgress } from '../common/CircularProgress'
import { getCropDisplayInfo } from '../../types/field'
import { calculateLotProgress } from '../../utils/lotProgress'
import type { Plot, GrowthStage } from '../../types'

// AGC: Weather data for lot card (will be fetched from API in future)
interface LotWeatherData {
  temperature: number       // AGC: Current temperature in °C
  rain24h: number          // AGC: Accumulated rain last 24h in mm
}

// AGC: Phenological stage display info
const GROWTH_STAGE_DISPLAY: Record<GrowthStage, { label: string; shortLabel: string }> = {
  'plantio': { label: 'Plantio', shortLabel: 'PL' },
  'germinacao': { label: 'Germinação', shortLabel: 'GE' },
  'vegetativo': { label: 'Vegetativo', shortLabel: 'VE' },
  'floracao': { label: 'Floração', shortLabel: 'FL' },
  'frutificacao': { label: 'Frutificação', shortLabel: 'FR' },
  'maturacao': { label: 'Maturação', shortLabel: 'MA' },
  'colheita': { label: 'Colheita', shortLabel: 'CO' },
}

interface LoteCardPremiumProps {
  lot: Plot
  onClick: () => void
  imageUrl?: string | null         // AGC: Cached crop image URL (from cache service)
  isImageLoading?: boolean         // AGC: Loading state for image
  weather?: LotWeatherData | null  // AGC: Weather data for the lot location
}

/**
 * AGC: Premium LOD card component
 * AGC: Features:
 * AGC: - Full-bleed background image (wheat/soy/corn/etc)
 * AGC: - Dark gradient overlay for text readability
 * AGC: - White text hierarchy (name, crop, area)
 * AGC: - Circular progress indicator (no ticks/needle)
 * AGC: - Weather info (temperature + rain 24h)
 * AGC: - Phenological stage with icon
 * AGC: - DJI Action 5 Pro inspired minimal style
 */
export const LoteCardPremium: React.FC<LoteCardPremiumProps> = ({ 
  lot, 
  onClick,
  imageUrl,
  isImageLoading = false,
  weather,
}) => {
  const [imageError, setImageError] = useState(false)
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const progress = useMemo(() => calculateLotProgress(lot), [lot])
  
  // AGC: Determine if we should show image or fallback
  const showImage = imageUrl && !imageError && !isImageLoading
  const showFallback = !showImage
  
  const hasWeather = weather !== null && weather !== undefined
  
  // AGC: Get phenological stage display info
  const stageInfo = GROWTH_STAGE_DISPLAY[lot.growthStage] || { label: 'Desconhecido', shortLabel: '?' }
  
  // AGC: Format area (assuming area is in hectares)
  const formatArea = (area: number) => {
    return `${area.toFixed(1)} ha`
  }
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left relative h-[140px] md:h-[160px] rounded-2xl overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] active:ring-4 active:ring-[var(--ring-click-primary)] group shadow-lg"
    >
      {/* AGC: Background Layer - Image or Fallback */}
      {showImage ? (
        <img 
          src={imageUrl}
          alt={cropDisplay.name}
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={() => setImageError(true)}
        />
      ) : (
        // AGC: Fallback - Dark gradient background with crop icon
        <div 
          className="absolute inset-0 bg-gradient-to-br flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, #1a1a2e 0%, #0f172a 100%)`
          }}
        >
          {isImageLoading ? (
            // AGC: Loading shimmer
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" />
          ) : (
            // AGC: Crop icon as fallback
            <span className="text-6xl opacity-20" style={{ color: cropDisplay.color }}>
              {cropDisplay.icon}
            </span>
          )}
        </div>
      )}
      
      {/* AGC: Dark Gradient Overlay - Ensures white text readability */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.2) 40%, rgba(0, 0, 0, 0.65) 100%)'
        }}
      />
      
      {/* AGC: Content Overlay - White text hierarchy */}
      <div className="relative z-10 h-full p-3 flex flex-col justify-between">
        {/* AGC: Top Section - Weather info + Progress */}
        <div className="flex justify-between items-start">
          {/* AGC: Left - Weather info (temperature + rain 24h) */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1">
              <Thermometer className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-xs font-semibold text-white">{hasWeather ? `${weather.temperature}°C` : '--°C'}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1">
              <CloudRain className="w-3.5 h-3.5 text-blue-300" />
              <span className="text-xs font-semibold text-white">{hasWeather ? `${weather.rain24h}mm` : '--mm'}</span>
            </div>
          </div>
          
          {/* AGC: Right - Circular progress */}
          <CircularProgress 
            progress={progress} 
            size={44}
            strokeWidth={4}
            showLabel={true}
          />
        </div>
        
        {/* AGC: Bottom Section - LOD Info + Phenological Stage */}
        <div className="flex justify-between items-end">
          {/* AGC: Left - LOT info */}
          <div className="space-y-0.5 flex-1 min-w-0 mr-3">
            {/* AGC: Primary - LOD Name */}
            <h3 className="text-base font-semibold text-white leading-tight truncate">
              {lot.name}
            </h3>
            
            {/* AGC: Secondary - Crop Name + Area */}
            <p className="text-xs font-medium text-white/80">
              {cropDisplay.name} • {formatArea(lot.area)}
            </p>
          </div>
          
          {/* AGC: Right - Phenological Stage Badge - Enlarged with glassmorphism */}
          <div className="flex items-center gap-2 bg-white/70 backdrop-blur-xl rounded-lg px-3 py-2 shadow-lg border border-white/30">
            <Sprout className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-gray-700">{stageInfo.shortLabel}</span>
          </div>
        </div>
      </div>
      
      {/* AGC: Hover State - Subtle shadow boost */}
      <div className="absolute inset-0 rounded-2xl shadow-lg group-hover:shadow-2xl transition-shadow duration-200 pointer-events-none" />
    </button>
  )
}

export default LoteCardPremium
